/**
 * Área FICHA DA CLIENTE: a moldura da ficha e as oito abas da frente A2 (Questionário Geral,
 * Personalidade, Radar de Estilo Raiz, Checklist de Atendimento, Checklist de Milhões, Coloração,
 * Lista de Desejos e Questionário Avaliativo).
 *
 * Toda leitura e gravação de aba passa pelo ESCOPO (cliente e, quando há, o lead aberto), como o
 * `comEscopo` e o `registroComLead` do legado (`admin.html:3320-3330`): com dois atendimentos, cada
 * um tem a sua ficha, e gravar sem o lead misturaria as duas.
 *
 * As abas das outras frentes (Arquivos, Anotações, Dossiê, IA, Mensagens, E-mail, Manual e
 * Histórico) têm módulo próprio; este arquivo não as cobre.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  comEscopo,
  executar,
  registroComLead,
  type Escopo,
  type FonteDeDados,
  type LinhaChecklistAtendimento,
  type LinhaChecklistColoracao,
  type LinhaJornadaCliente,
  type LinhaLeadsFunil,
  type LinhaListaDesejos,
  type LinhaParametrosProdutoAba,
  type LinhaQuestionarioAvaliativo,
  type LinhaQuestionarioGeral,
  type LinhaQuestionarioPersonalidade,
  type LinhaRoteiroProdutos,
  type LinhaUltimoAcesso,
  type LinhaVigenciaAcompanhamentoAnual,
  type Resultado,
} from '../nucleo.js';

/* ===========================================================================
   A MOLDURA
   =========================================================================== */

/**
 * O lead aberto na ficha: as colunas da escolha "Qual atendimento?" (`admin.html:5878`) MAIS a
 * origem.
 *
 * A ORIGEM ENTRA AQUI porque o Histórico escreve "Lead criada · origem: X" (`admin.html:5663-5666`)
 * e no legado ela chega quando a ficha é aberta pelo Kanban (`select('*')`, `6511`) ou pelo
 * compromisso (`4540`). Pela lista de clientes o legado lê só as quatro colunas, e ali o Histórico
 * escreve "Lead criada" sem origem: quem apaga a origem nesse caminho é a moldura
 * (`telas/ficha-cliente/index.tsx`), e não esta leitura, que é a mesma para todos os caminhos.
 */
export type LeadDaFicha = Pick<LinhaLeadsFunil, 'id' | 'produtos' | 'status' | 'data_criacao' | 'origem'>;

/** O cadastro lido pelo questionário, quando a cliente não tem perfil (`admin.html:6942`). */
export type CadastroPeloQuestionario = Pick<LinhaQuestionarioGeral, 'user_id' | 'nome_completo' | 'email' | 'telefone'>;

/**
 * Um lead específico da cliente, para a ficha aberta com o atendimento já escolhido.
 *
 * O legado não relê o lead: ele passa o objeto que já tinha em memória (`abrirCliente(cliente,
 * lead)`, `admin.html:5913`). Com endereço próprio, a ficha relê as MESMAS colunas da escolha
 * (`admin.html:5878`), e o filtro pela cliente impede abrir o lead de outra pessoa pelo endereço.
 */
export function lerLeadDaFicha(
  fonte: FonteDeDados,
  userId: string,
  leadId: number,
): Promise<Resultado<LeadDaFicha | null>> {
  return executar<LeadDaFicha | null>(
    clienteDe(fonte)
      .from('leads_funil')
      // A origem vem junto, para o Histórico poder escrever "Lead criada · origem: X" nos caminhos
      // em que o legado a tem (ver `LeadDaFicha`).
      .select('id, produtos, status, data_criacao, origem')
      // O lead pedido...
      .eq('id', leadId)
      // ...e só se ele for desta cliente.
      .eq('user_id_vinculado', userId)
      // `maybeSingle`: lead inexistente ou de outra cliente volta `null`, e não erro.
      .maybeSingle(),
  );
}

/**
 * O cadastro pelo questionário, para a cliente que não está na lista.
 * Legado: `admin.html:6942` (`abrirClienteDoLead`), `sb.from('questionario_geral').select('user_id,
 * nome_completo, email, telefone').eq('user_id', userId).maybeSingle()`.
 */
export function lerCadastroPeloQuestionario(
  fonte: FonteDeDados,
  userId: string,
): Promise<Resultado<CadastroPeloQuestionario | null>> {
  return executar<CadastroPeloQuestionario | null>(
    clienteDe(fonte)
      .from('questionario_geral')
      .select('user_id, nome_completo, email, telefone')
      .eq('user_id', userId)
      .maybeSingle(),
  );
}

/**
 * Quando a cliente entrou no portal pela última vez. Só lê, nunca grava: gravar aqui apagaria o
 * acesso real da cliente com o acesso da consultora (comentário de `admin.html:5919-5921`).
 * Legado: `admin.html:5937`, `sb.from('ultimo_acesso').select('acessado_em').eq('user_id', userId)
 * .maybeSingle()`.
 */
export function lerUltimoAcessoDaCliente(
  fonte: FonteDeDados,
  userId: string,
): Promise<Resultado<Pick<LinhaUltimoAcesso, 'acessado_em'> | null>> {
  return executar<Pick<LinhaUltimoAcesso, 'acessado_em'> | null>(
    clienteDe(fonte).from('ultimo_acesso').select('acessado_em').eq('user_id', userId).maybeSingle(),
  );
}

/**
 * A sequência de produtos que alimenta o "Próximo passo sugerido".
 * Legado: `admin.html:9470`, `sb.from('roteiro_produtos').select('*').order('ordem')`, lida na
 * entrada do painel (`admin.html:3505`) e usada em `calcularProximoProdutoSugerido` (`9524`).
 */
export function lerRoteiroProdutos(fonte: FonteDeDados): Promise<Resultado<LinhaRoteiroProdutos[]>> {
  return executar<LinhaRoteiroProdutos[]>(clienteDe(fonte).from('roteiro_produtos').select('*').order('ordem'));
}

/**
 * A vigência do Acompanhamento Anual do lead aberto.
 * Legado: `admin.html:6035`, `comEscopo(sb.from('vigencia_acompanhamento_anual').select('*'))
 * .maybeSingle()`.
 */
export function lerVigencia(
  fonte: FonteDeDados,
  escopo: Escopo,
): Promise<Resultado<LinhaVigenciaAcompanhamentoAnual | null>> {
  return executar<LinhaVigenciaAcompanhamentoAnual | null>(
    comEscopo(clienteDe(fonte).from('vigencia_acompanhamento_anual').select('*'), escopo).maybeSingle(),
  );
}

/** O que a tela manda gravar na vigência: as quatro colunas de `admin.html:6074-6079`. */
export interface CamposDaVigencia {
  // AAAA-MM-DD do início do ciclo.
  readonly data_inicio: string;
  // AAAA-MM-DD da renovação, um ano depois.
  readonly data_renovacao: string;
  // Zerado a cada ciclo, para o próximo lembrete valer.
  readonly lembrete_enviado_em: null;
  // A hora da gravação, que a tela mede.
  readonly atualizado_em: string;
}

/**
 * Define ou renova o ciclo do Acompanhamento Anual, e devolve a linha gravada.
 * Legado: `admin.html:6080`, `sb.from('vigencia_acompanhamento_anual').upsert(registroComLead(...),
 * { onConflict: 'user_id,lead_id' }).select().single()`.
 */
export function salvarVigencia(
  fonte: FonteDeDados,
  escopo: Escopo,
  campos: CamposDaVigencia,
): Promise<Resultado<LinhaVigenciaAcompanhamentoAnual>> {
  return executar<LinhaVigenciaAcompanhamentoAnual>(
    clienteDe(fonte)
      .from('vigencia_acompanhamento_anual')
      // O registro leva `user_id` e `lead_id` do escopo, como o `registroComLead` do legado.
      .upsert(registroComLead(escopo, campos), { onConflict: 'user_id,lead_id' })
      // Depois da gravação, a leitura da linha gravada é a do legado.
      .select()
      .single(),
  );
}

/**
 * As abas que os produtos do lead habilitam para a consultora, com a ordem de cada uma.
 * Legado: `admin.html:6093`, `sb.from('parametros_produto_aba').select('aba, ordem')
 * .in('produto', paramKeys).in('visibilidade', ['ambos', 'administradora'])`.
 */
export function lerAbasDosProdutos(
  fonte: FonteDeDados,
  produtos: readonly string[],
): Promise<Resultado<Pick<LinhaParametrosProdutoAba, 'aba' | 'ordem'>[]>> {
  return executar<Pick<LinhaParametrosProdutoAba, 'aba' | 'ordem'>[]>(
    clienteDe(fonte)
      .from('parametros_produto_aba')
      .select('aba, ordem')
      // As chaves de parâmetro dos produtos do lead, já traduzidas pela tela.
      .in('produto', [...produtos])
      // Só o que a consultora vê: "ambos" e "administradora" (a visibilidade só da cliente fica fora).
      .in('visibilidade', ['ambos', 'administradora']),
  );
}

/**
 * A etapa da jornada pós-venda do lead aberto.
 * Legado: `admin.html:5630`, `comEscopo(sb.from('jornada_cliente').select('etapa')).maybeSingle()`.
 */
export function lerJornada(
  fonte: FonteDeDados,
  escopo: Escopo,
): Promise<Resultado<Pick<LinhaJornadaCliente, 'etapa'> | null>> {
  return executar<Pick<LinhaJornadaCliente, 'etapa'> | null>(
    comEscopo(clienteDe(fonte).from('jornada_cliente').select('etapa'), escopo).maybeSingle(),
  );
}

/**
 * Muda a etapa da jornada. O histórico é gravado por gatilho no banco
 * (`trg_registrar_historico_jornada`, comentário de `admin.html:5651-5655`), e não aqui.
 * Legado: `admin.html:5640`, `sb.from('jornada_cliente').upsert(registroComLead({ etapa,
 * atualizado_em }), { onConflict: 'user_id,lead_id' })`.
 */
export function gravarEtapaDaJornada(
  fonte: FonteDeDados,
  escopo: Escopo,
  etapa: string,
  atualizadoEm: string,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte)
      .from('jornada_cliente')
      .upsert(registroComLead(escopo, { etapa, atualizado_em: atualizadoEm }), { onConflict: 'user_id,lead_id' }),
  );
}

/**
 * Os leads vinculados à cliente, com o nome e o contato do Funil, para a ficha montar o cabeçalho
 * com a MESMA regra da lista. É a leitura de `admin.html:3574` com a lista de uma cliente só.
 */
export { lerLeadsDaLista as lerLeadsDaClienteParaCabecalho } from './clientes.js';

/* ===========================================================================
   AS ABAS
   =========================================================================== */

/**
 * O valor de uma pergunta de marcar várias do Questionário Geral (23a, 23b, 23c e 25).
 *
 * O LEGADO GRAVA LISTA, e o banco de homologação declara a coluna como `text`
 * (`supabase/migrations/20260812_020_cliente.sql:40`). O PostgREST aceita a lista e a guarda como
 * texto. O legado lê com `valores.includes(opcao)`, que funciona nos dois casos (em lista, compara
 * itens; em texto, procura o trecho). Por isso o tipo aceita os dois, e a tela reproduz o
 * `includes` do legado, sem converter nada.
 */
export type ListaOuTexto = readonly string[] | string | null;

/** A linha do Questionário Geral com as quatro perguntas de marcar como o legado as trata. */
export type LinhaGeralDaFicha = Omit<
  LinhaQuestionarioGeral,
  'imagem_desejada' | 'sensacao_desejada' | 'objetivos_momento' | 'dificuldades_atuais'
> & {
  readonly imagem_desejada: ListaOuTexto;
  readonly sensacao_desejada: ListaOuTexto;
  readonly objetivos_momento: ListaOuTexto;
  readonly dificuldades_atuais: ListaOuTexto;
};

/** A linha de Milhões: as colunas booleanas do catálogo fixo, lidas por nome (`admin.html:5375`). */
export type LinhaMilhoesDaFicha = Record<string, unknown>;

/** O que a tela manda gravar numa aba: as colunas da aba, sem `user_id` e `lead_id`. */
export type CamposDaAba = Readonly<Record<string, unknown>>;

// Lê a linha única de uma tabela da ficha no escopo aberto, como `comEscopo(sb.from(t)
// .select('*')).maybeSingle()`, que é a mesma leitura em todas as abas deste arquivo.
function lerLinhaDaAba<T>(fonte: FonteDeDados, tabela: string, escopo: Escopo): Promise<Resultado<T | null>> {
  // `maybeSingle`: aba que a cliente ainda não respondeu volta `null`, e a tela mostra o campo vazio.
  return executar<T | null>(comEscopo(clienteDe(fonte).from(tabela).select('*'), escopo).maybeSingle());
}

// Grava a linha de uma tabela da ficha no escopo aberto, como `sb.from(t).upsert(registroComLead(
// campos), { onConflict: 'user_id,lead_id' })`, que é a mesma gravação em todas as abas.
function gravarLinhaDaAba(
  fonte: FonteDeDados,
  tabela: string,
  escopo: Escopo,
  campos: CamposDaAba,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte).from(tabela).upsert(registroComLead(escopo, campos), { onConflict: 'user_id,lead_id' }),
  );
}

/** Legado: `admin.html:5023`, `comEscopo(sb.from('questionario_geral').select('*')).maybeSingle()`. */
export function lerQuestionarioGeral(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<LinhaGeralDaFicha | null>> {
  return lerLinhaDaAba<LinhaGeralDaFicha>(fonte, 'questionario_geral', escopo);
}

/** Legado: `admin.html:4881`, `sb.from('questionario_geral').upsert(registro, { onConflict: 'user_id,lead_id' })`. */
export function salvarQuestionarioGeral(fonte: FonteDeDados, escopo: Escopo, campos: CamposDaAba): Promise<Resultado<null>> {
  return gravarLinhaDaAba(fonte, 'questionario_geral', escopo, campos);
}

/** Legado: `admin.html:5030`, `comEscopo(sb.from('questionario_personalidade').select('*')).maybeSingle()`. */
export function lerPersonalidade(
  fonte: FonteDeDados,
  escopo: Escopo,
): Promise<Resultado<LinhaQuestionarioPersonalidade | null>> {
  return lerLinhaDaAba<LinhaQuestionarioPersonalidade>(fonte, 'questionario_personalidade', escopo);
}

/** Legado: `admin.html:4912`, `sb.from('questionario_personalidade').upsert(registro, { onConflict: 'user_id,lead_id' })`. */
export function salvarPersonalidade(fonte: FonteDeDados, escopo: Escopo, campos: CamposDaAba): Promise<Resultado<null>> {
  return gravarLinhaDaAba(fonte, 'questionario_personalidade', escopo, campos);
}

/** Legado: `admin.html:5037`, `comEscopo(sb.from('questionario_avaliativo').select('*')).maybeSingle()`. */
export function lerAvaliativo(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<LinhaQuestionarioAvaliativo | null>> {
  return lerLinhaDaAba<LinhaQuestionarioAvaliativo>(fonte, 'questionario_avaliativo', escopo);
}

/** Legado: `admin.html:5143`, `sb.from('questionario_avaliativo').upsert(registro, { onConflict: 'user_id,lead_id' })`. */
export function salvarAvaliativo(fonte: FonteDeDados, escopo: Escopo, campos: CamposDaAba): Promise<Resultado<null>> {
  return gravarLinhaDaAba(fonte, 'questionario_avaliativo', escopo, campos);
}

/** Legado: `admin.html:5341`, `comEscopo(sb.from('checklist_atendimento').select('*')).maybeSingle()`. */
export function lerChecklistAtendimento(
  fonte: FonteDeDados,
  escopo: Escopo,
): Promise<Resultado<LinhaChecklistAtendimento | null>> {
  return lerLinhaDaAba<LinhaChecklistAtendimento>(fonte, 'checklist_atendimento', escopo);
}

/** Legado: `admin.html:5359`, `sb.from('checklist_atendimento').upsert(registro, { onConflict: 'user_id,lead_id' })`. */
export function salvarChecklistAtendimento(fonte: FonteDeDados, escopo: Escopo, campos: CamposDaAba): Promise<Resultado<null>> {
  return gravarLinhaDaAba(fonte, 'checklist_atendimento', escopo, campos);
}

/** Legado: `admin.html:5367`, `comEscopo(sb.from('checklist_milhoes').select('*')).maybeSingle()`. */
export function lerChecklistMilhoes(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<LinhaMilhoesDaFicha | null>> {
  return lerLinhaDaAba<LinhaMilhoesDaFicha>(fonte, 'checklist_milhoes', escopo);
}

/** Legado: `admin.html:5385`, `sb.from('checklist_milhoes').upsert(registro, { onConflict: 'user_id,lead_id' })`. */
export function salvarChecklistMilhoes(fonte: FonteDeDados, escopo: Escopo, campos: CamposDaAba): Promise<Resultado<null>> {
  return gravarLinhaDaAba(fonte, 'checklist_milhoes', escopo, campos);
}

/** Legado: `admin.html:5276`, `comEscopo(sb.from('checklist_coloracao').select('*')).maybeSingle()`. */
export function lerColoracao(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<LinhaChecklistColoracao | null>> {
  return lerLinhaDaAba<LinhaChecklistColoracao>(fonte, 'checklist_coloracao', escopo);
}

/** Legado: `admin.html:5332`, `sb.from('checklist_coloracao').upsert(registro, { onConflict: 'user_id,lead_id' })`. */
export function salvarColoracao(fonte: FonteDeDados, escopo: Escopo, campos: CamposDaAba): Promise<Resultado<null>> {
  return gravarLinhaDaAba(fonte, 'checklist_coloracao', escopo, campos);
}

/** Legado: `admin.html:5393`, `comEscopo(sb.from('lista_desejos').select('*')).maybeSingle()`. */
export function lerListaDesejos(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<LinhaListaDesejos | null>> {
  return lerLinhaDaAba<LinhaListaDesejos>(fonte, 'lista_desejos', escopo);
}

/** Legado: `admin.html:5411`, `sb.from('lista_desejos').upsert(registroComLead({ conteudo, atualizado_em }), { onConflict: 'user_id,lead_id' })`. */
export function salvarListaDesejos(fonte: FonteDeDados, escopo: Escopo, campos: CamposDaAba): Promise<Resultado<null>> {
  return gravarLinhaDaAba(fonte, 'lista_desejos', escopo, campos);
}
